/**
 * Shared CSRF double-submit helper.
 *
 * Backend contract (`macdm/api/auth.py::_check_csrf`): every state-mutating
 * request (anything but GET/HEAD/OPTIONS) must carry an `X-Macdm-Csrf`
 * header whose value matches the readable `macdm_csrf` cookie set at
 * login. The check is only skipped when the orchestrator runs with
 * `MACDM_DEV_MODE=1` — production always enforces it.
 *
 * Every transport module that issues a mutating `fetch`/`XMLHttpRequest`
 * MUST route through `getCsrfToken()` / `withCsrfHeader()` here instead of
 * re-deriving the cookie inline; a single source of truth prevents the
 * "works in dev, 403s in prod" drift that happens when a new transport
 * file is added without wiring the header.
 */

const CSRF_COOKIE_NAME = "macdm_csrf";
const CSRF_HEADER_NAME = "X-Macdm-Csrf";

export function getCsrfToken(): string | null {
  return (
    document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${CSRF_COOKIE_NAME}=`))
      ?.split("=")[1] ?? null
  );
}

/** Merge the CSRF header into an existing headers map (no-op if no cookie yet, e.g. pre-login). */
export function withCsrfHeader(
  headers: Record<string, string> = {},
): Record<string, string> {
  const token = getCsrfToken();
  return token ? { ...headers, [CSRF_HEADER_NAME]: token } : headers;
}

export { CSRF_HEADER_NAME };
